import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { chatKeys } from '~/lib/query-keys';

/** The Work container (a project) a chat is about — mirrors the GET route's
 *  `entity` shape (`src/agents/chat-threads-routes.ts`). */
export interface ChatScopeEntity {
  id: string;
  name: string;
  type: 'project';
  archived: boolean;
}

/**
 * What the server has told us about a chat's scope, keeping two states that
 * used to collapse into the same value distinct:
 *
 *  - `known: true` — a completed `200`. `entity` is the real answer, and may
 *    itself legitimately be `null` (the chat genuinely has no scope, e.g.
 *    right after an Eddy-driven "remove"). This is authoritative and must
 *    win over anything a caller merely guessed at mount time.
 *  - `known: false` — a `404`. The chat has no OWNED `chat_sessions` row yet
 *    (see the queryFn below), so the server hasn't actually answered the
 *    question at all — there is no "confirmed none" here, only "don't know
 *    yet".
 *
 * FRONT-1813 finding 1: the pre-fix version of this hook returned `entity |
 * null` for BOTH cases — a 404 and a genuine `{entity: null}` were
 * indistinguishable once through `queryFn`, both landing as a
 * server-confirmed `null`. A caller seeding the marker from a locally-known
 * value (`inbox-chat-modal.tsx`'s `initialScope`) had no way to tell "the
 * server just confirmed there's nothing here" apart from "the server hasn't
 * even created the row yet" — so ANY refetch trigger (mount, a browser
 * `online` event after a wifi flap/wake/VPN switch — `refetchOnReconnect`
 * defaults to on and nothing in this repo turns it off, an
 * `invalidateQueries` call, a future imperative refetch) that landed a 404
 * before the backend's own fire-and-forget scope persist finished silently
 * wiped the seed for the life of the modal. See `resolveEffectiveScope`
 * below for how keeping the two apart removes the race by construction
 * instead of suppressing one more of its triggers.
 */
export type ChatScopeResult = { known: true; entity: ChatScopeEntity | null } | { known: false };

function statusOf(err: unknown): number | undefined {
  return (err as { status?: number } | null)?.status;
}

/**
 * Matches the app-wide default in `lib/query-client.ts`'s `shouldRetry`
 * (401/403 never retry, everything else bounded at 3) plus 404 — this
 * route 404s BY DESIGN until the session row exists (see the queryFn
 * below), so retrying it would just spin forever on a blank "+" chat.
 */
function shouldRetryScope(failureCount: number, err: unknown): boolean {
  const status = statusOf(err);
  if (status === 401 || status === 403 || status === 404) return false;
  return failureCount < 3;
}

/**
 * FRONT-1813 finding 1: the marker's whole precedence rule, extracted as a
 * pure function so it can be unit-tested (`use-chat-scope.test.ts`) without
 * mounting `useChatScope`, TanStack, or `InboxChatModal` at all — this is
 * the exact logic that produced FRONT-1813's own regression once already (a
 * seed silently overwritten by a same-value-shaped but not-actually-the-
 * same-fact 404).
 *
 * `known` ⇒ trust the server's answer, even when that answer is a genuine
 * `entity: null` — it must win over a stale seed (e.g. the marker must
 * clear once Eddy's own `work_chat_move` tool removes the scope, even
 * though the modal originally minted with `initialScope` set). Anything
 * else — no result yet (still loading) or `known: false` (a 404: the
 * server hasn't actually confirmed anything) — falls back to the seed,
 * which is the only fact available until the server has genuinely spoken.
 * This holds no matter WHY the query re-ran (mount, reconnect, an
 * `invalidateQueries` call, a future imperative refetch): a `known: false`
 * result can never masquerade as a confirmed "no scope" again, so nothing
 * needs to suppress any particular refetch trigger to stay correct.
 */
export function resolveEffectiveScope(
  seed: ChatScopeEntity | null,
  result: ChatScopeResult | undefined,
): ChatScopeEntity | null {
  return result?.known ? result.entity : seed;
}

/**
 * FRONT-1191: which Work container (if any) a chat is scoped to — backs the
 * modal's "About: <name>" marker chip. `enabled` lets the caller gate this
 * off entirely (used for non-chat asset kinds, which have no scope at all).
 *
 * Always enabled once a session id exists — including for a chat that
 * mounts already knowing its scope (`inbox-chat-modal.tsx`'s
 * `initialScope`, from a chat just minted on the Projects board). There is
 * no seeding option on this hook any more: a caller that already knows the
 * answer at mount time passes it straight to `resolveEffectiveScope`
 * alongside this hook's own `data`, rather than through TanStack's
 * `initialData`/`refetchOnMount` machinery (see that function's docblock
 * for why — the discriminated `ChatScopeResult` above removes the race that
 * machinery used to exist to paper over). So this query behaves like any
 * ordinary query: it fetches on mount, on reconnect, on window focus per
 * the app-wide default, and on `invalidateQueries` — none of that can ever
 * overwrite a caller's seed with a false negative, because a 404 surfaces
 * as `known: false`, never as a confirmed empty scope.
 */
export function useChatScope(sessionId: string | null | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: chatKeys.scope(sessionId ?? ''),
    queryFn: async (): Promise<ChatScopeResult> => {
      try {
        const data = await api.get<{ entity: ChatScopeEntity | null }>(
          `/api/chat/sessions/${encodeURIComponent(sessionId as string)}/scope`,
        );
        return { known: true, entity: data.entity };
      } catch (err) {
        // The route 404s BY DESIGN until the session row exists (created by
        // `setSessionOwner` on the first user message) — a "+" chat that's
        // never been typed into has nothing to fetch yet. This is NOT the
        // same fact as a completed 200 saying "no scope" — see
        // `ChatScopeResult`'s own docblock — so it is surfaced as
        // `known: false`, never coerced into a confirmed `null`.
        if (statusOf(err) === 404) return { known: false };
        throw err;
      }
    },
    enabled: Boolean(sessionId) && (options?.enabled ?? true),
    retry: shouldRetryScope,
  });
}
